import { useMemo, useState } from 'react';
import type { DocumentFacetCountsDto, DocumentMachineFacetOptionDto } from '@/types/document';
import {
  SOURCE_FILTER_OPTIONS,
  formatDocumentTypeLabel,
  formatLanguageLabel,
  getSourceKindLabel,
} from './documentBrowserUtils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronDown, ChevronRight } from 'lucide-react';

type SidebarFilters = {
  status: string | null;
  sourceKind: string | null;
  documentType: string | null;
  language: string | null;
  machineModelId: string | null;
};

interface DocumentFilterSidebarProps {
  availableDocumentTypes: string[];
  availableLanguages: string[];
  availableMachines: DocumentMachineFacetOptionDto[];
  facetCounts: DocumentFacetCountsDto;
  filters: SidebarFilters;
  onStatusChange: (value: string | null) => void;
  onSourceChange: (value: string | null) => void;
  onDocumentTypeChange: (value: string | null) => void;
  onLanguageChange: (value: string | null) => void;
  onMachineChange: (value: string | null) => void;
  onReset: () => void;
}

const STATUS_OPTIONS = [
  { value: 'Completed', label: 'Aktiv' },
  { value: 'AwaitingConfirmation', label: 'Review' },
  { value: 'Uploaded', label: 'Neu' },
  { value: 'Parsing', label: 'Parsing' },
  { value: 'Extracting', label: 'Extraktion' },
  { value: 'Failed', label: 'Fehler' },
  { value: 'PartiallyFailed', label: 'Teilfehler' },
] as const;

type SectionKey = 'status' | 'documentType' | 'machine' | 'language' | 'source';

const FACET_ITEM_TEXT_CLASS = 'text-xs';
const FACET_EMPTY_STATE_CLASS = `px-3 py-1 ${FACET_ITEM_TEXT_CLASS} text-muted-foreground`;

function SidebarSection({
  title,
  collapsed,
  onToggle,
  children,
}: {
  title: string;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={!collapsed}
      >
        <span className="text-sm font-semibold uppercase tracking-[0.28em] text-muted-foreground">{title}</span>
        {collapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {!collapsed && <div className="space-y-1.5">{children}</div>}
    </section>
  );
}

function FacetButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        `flex w-full items-center justify-between rounded-xl px-3 py-2 ${FACET_ITEM_TEXT_CLASS} transition-colors`,
        active ? 'bg-foreground text-background shadow-sm' : 'text-foreground hover:bg-muted/70',
      ].join(' ')}
      aria-pressed={active}
    >
      <span className={`truncate pr-3 ${FACET_ITEM_TEXT_CLASS}`}>{label}</span>
      <Badge
        variant="secondary"
        className={[
          'min-w-7 justify-center rounded-full px-2 py-0 text-[11px]',
          active ? 'bg-background/15 text-background' : 'bg-muted text-muted-foreground',
        ].join(' ')}
      >
        {count}
      </Badge>
    </button>
  );
}

export function DocumentFilterSidebar({
  availableDocumentTypes,
  availableLanguages,
  availableMachines,
  facetCounts,
  filters,
  onStatusChange,
  onSourceChange,
  onDocumentTypeChange,
  onLanguageChange,
  onMachineChange,
  onReset,
}: DocumentFilterSidebarProps) {
  const [collapsedSections, setCollapsedSections] = useState<Record<SectionKey, boolean>>({
    status: false,
    documentType: false,
    machine: false,
    language: false,
    source: false,
  });

  const visibleDocumentTypes = useMemo(() => {
    return availableDocumentTypes.filter((value) => (facetCounts.documentTypes[value] ?? 0) > 0 || filters.documentType === value);
  }, [availableDocumentTypes, facetCounts.documentTypes, filters.documentType]);

  const visibleLanguages = useMemo(() => {
    return availableLanguages.filter((value) => (facetCounts.languages[value] ?? 0) > 0 || filters.language === value);
  }, [availableLanguages, facetCounts.languages, filters.language]);

  const visibleMachines = useMemo(() => {
    return availableMachines.filter((option) => (facetCounts.machines[option.value] ?? 0) > 0 || filters.machineModelId === option.value);
  }, [availableMachines, facetCounts.machines, filters.machineModelId]);

  const toggleSection = (key: SectionKey) => {
    setCollapsedSections((state) => ({
      ...state,
      [key]: !state[key],
    }));
  };

  const hasFilters = !!(filters.status || filters.sourceKind || filters.documentType || filters.language || filters.machineModelId);

  return (
    <Card className="border-border/70 bg-card/95 shadow-sm">
      <CardContent className="space-y-6 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground">Filter</p>
            <p className="text-xs text-muted-foreground">Filter für die aktuelle Dokumentauswahl</p>
          </div>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={onReset}>
              Reset
            </Button>
          )}
        </div>

        <SidebarSection title="Status" collapsed={collapsedSections.status} onToggle={() => toggleSection('status')}>
          {STATUS_OPTIONS.filter((option) => (facetCounts.statuses[option.value] ?? 0) > 0 || filters.status === option.value).map((option) => (
            <FacetButton
              key={option.value}
              label={option.label}
              count={facetCounts.statuses[option.value] ?? 0}
              active={filters.status === option.value}
              onClick={() => onStatusChange(filters.status === option.value ? null : option.value)}
            />
          ))}
        </SidebarSection>

        <SidebarSection title="Dokumenttyp" collapsed={collapsedSections.documentType} onToggle={() => toggleSection('documentType')}>
          {visibleDocumentTypes.length > 0 ? visibleDocumentTypes.map((value) => (
            <FacetButton
              key={value}
              label={formatDocumentTypeLabel(value)}
              count={facetCounts.documentTypes[value] ?? 0}
              active={filters.documentType === value}
              onClick={() => onDocumentTypeChange(filters.documentType === value ? null : value)}
            />
          )) : <p className={FACET_EMPTY_STATE_CLASS}>Noch keine Dokumenttypen verfuegbar.</p>}
        </SidebarSection>

        <SidebarSection title="Maschine" collapsed={collapsedSections.machine} onToggle={() => toggleSection('machine')}>
          {visibleMachines.length > 0 ? visibleMachines.map((option) => (
            <FacetButton
              key={option.value}
              label={option.label}
              count={facetCounts.machines[option.value] ?? 0}
              active={filters.machineModelId === option.value}
              onClick={() => onMachineChange(filters.machineModelId === option.value ? null : option.value)}
            />
          )) : <p className={FACET_EMPTY_STATE_CLASS}>Noch keine Maschinenzuordnungen verfuegbar.</p>}
        </SidebarSection>

        <SidebarSection title="Sprache" collapsed={collapsedSections.language} onToggle={() => toggleSection('language')}>
          {visibleLanguages.length > 0 ? visibleLanguages.map((value) => (
            <FacetButton
              key={value}
              label={formatLanguageLabel(value)}
              count={facetCounts.languages[value] ?? 0}
              active={filters.language === value}
              onClick={() => onLanguageChange(filters.language === value ? null : value)}
            />
          )) : <p className={FACET_EMPTY_STATE_CLASS}>Noch keine Sprachen verfuegbar.</p>}
        </SidebarSection>

        <SidebarSection title="Quelle" collapsed={collapsedSections.source} onToggle={() => toggleSection('source')}>
          {SOURCE_FILTER_OPTIONS.filter((option) => (facetCounts.sourceKinds[option.value] ?? 0) > 0 || filters.sourceKind === option.value).map((option) => (
            <FacetButton
              key={option.value}
              label={option.value === 'connector' ? 'Connector' : getSourceKindLabel(option.value)}
              count={facetCounts.sourceKinds[option.value] ?? 0}
              active={filters.sourceKind === option.value}
              onClick={() => onSourceChange(filters.sourceKind === option.value ? null : option.value)}
            />
          ))}
        </SidebarSection>
      </CardContent>
    </Card>
  );
}